/**
 * QUEM USA, QUEM PAGA: o grupo inteiro de relance (passo 2 da prévia
 * https://claude.ai/artifact/LhzQ3vJtx7KauauYQ54t3i).
 *
 * Até 10/2026 o total de ramais do grupo só existia dentro do modal de edição
 * de UMA empresa ("arca com 68,89% por ter 31 dos 45 ramais do grupo"). Para
 * ver o grupo era preciso abrir um modal por empresa e somar na mão.
 *
 * ⛔ NENHUM VALOR AQUI É CALCULADO. Ramais, percentual e cota vêm da API
 * (`GET /clients/:id/rateio`): `base[].ramais`, `linhas[].percentualVigente`,
 * `linhas[].valorVigente`. A produção cobra ITEM A ITEM, e dividir o pool de uma
 * vez dá outro centavo (CONCEITO: 352,92/159,38 pelo pool, 352,91/159,39 item a
 * item, que é o cobrado). Também não há regra de resíduo: a divisão por pesos
 * do backend já fecha exato (PARANAFRIGOR soma 438,50).
 *
 * Só a contagem de DDR é feita aqui, porque a API não a devolve: é a quantidade
 * CONTRATADA (itens de DDR das assinaturas ativas), coerente com os ramais, que
 * também são os contratados. O cadastro de DDRs pode divergir (na CONCEITO, em
 * 10/2026, o cadastro tinha 3 na matriz e 0 na PRÉ-FABRICADOS; os contratos
 * cobram 2 e 1).
 */
import { ComRateio, RateioDoGrupo } from './rateioDoGrupo';
import { tipoDeVinculo } from './grupoEconomico';
import { mrrDaEmpresa, mrrDasEmpresas } from './mrrDoGrupo';

export type PapelNoGrupo = 'matriz' | 'filial' | 'coligada' | 'empresa';

export interface EmpresaNoPainel {
  clientId: number;
  ramais: number;
  ddrs: number;
  percentual: number;
  /** Cota no compartilhado, da API. */
  valor: number;
  /** Divisão que a base de HOJE daria (API: `percentualCalculado`, `valorCalculado`).
   *  Só difere do vigente quando a base mudou depois do acordo (`divergente`). */
  percentualCalculado: number;
  valorCalculado: number;
  /** calculado − vigente, em reais e com sinal (API: `deltaReais`). */
  deltaReais: number;
  /** MRR das assinaturas ativas DESTA empresa (cota + o que é próprio dela). */
  mrr: number;
  ehMatriz: boolean;
  /** Mesma regra da tela de Grupos Econômicos (`tipoDeVinculo`, raiz do CNPJ). */
  papel: PapelNoGrupo;
  cnpj: string | null;
}

export interface PainelDoGrupo {
  ramais: number;
  ddrs: number;
  pool: number;
  /** Soma do MRR das assinaturas ativas das empresas do rateio. */
  mrr: number;
  /** MRR − compartilhado: o que cada empresa paga só por si. */
  proprio: number;
  empresas: EmpresaNoPainel[];
  /** A base mudou: a divisão vigente foi acordada com outra contagem (API: `divergente`). */
  divergente: boolean;
}

interface ContratoComItens {
  clientId: number;
  status: string;
  mrr?: number | string | null;
  items?: Array<{ productCode?: string | null; quantity?: number | string | null }>;
}

interface ClienteDoGrupo {
  id: number;
  cnpj?: string | null;
}

// A conta do MRR mora em utils/mrrDoGrupo (uma só para todas as telas).
export { mrrDaEmpresa } from './mrrDoGrupo';

/** DDRs contratados de uma empresa: quantidade dos itens de DDR das assinaturas ativas. */
export function ddrsContratados(contracts: ContratoComItens[], clientId: number): number {
  return contracts
    .filter((c) => c.clientId === clientId && c.status === 'Ativo')
    .flatMap((c) => c.items || [])
    .filter((i) => /DDR/i.test(String(i.productCode || '')))
    .reduce((s, i) => s + (Number(i.quantity) || 0), 0);
}

/**
 * null quando o grupo não tem rateio: sem cota, não há o que mostrar.
 *
 * ⛔ UM CAMINHO SÓ. O painel de Grupos Econômicos e a aba "O grupo" da ficha da
 * assinatura chamam esta função. Se cada tela calculasse por um lado, em três
 * meses uma mostraria um número e a outra outro.
 */
export function montarPainelDoGrupo(r: RateioDoGrupo | null, contracts: ContratoComItens[], clientes: ClienteDoGrupo[] = []): PainelDoGrupo | null {
  if (!r || r.temRateio === false) return null;
  const rr = r as ComRateio;
  const ramaisDe = (id: number) => Number(rr.base.find((b) => b.clientId === id)?.ramais) || 0;
  const cnpjDe = (id: number) => clientes.find((c) => c.id === id)?.cnpj || null;
  const cnpjMatriz = cnpjDe(rr.matrizId);
  const papelDe = (id: number): PapelNoGrupo => {
    if (id === rr.matrizId) return 'matriz';
    return tipoDeVinculo(cnpjMatriz, cnpjDe(id)) || 'empresa';
  };
  const empresas: EmpresaNoPainel[] = rr.linhas.map((l) => ({
    clientId: l.clientId,
    ramais: ramaisDe(l.clientId),
    ddrs: ddrsContratados(contracts, l.clientId),
    percentual: l.percentualVigente,
    valor: l.valorCobrado,
    percentualCalculado: l.percentualCalculado,
    valorCalculado: l.valorCalculado,
    deltaReais: l.deltaReais,
    mrr: mrrDaEmpresa(contracts, l.clientId),
    ehMatriz: l.clientId === rr.matrizId,
    papel: papelDe(l.clientId),
    cnpj: cnpjDe(l.clientId),
  }));
  // Quem mais usa primeiro; empate, quem mais paga.
  empresas.sort((a, b) => b.ramais - a.ramais || b.valor - a.valor);
  const mrr = mrrDasEmpresas(contracts, empresas.map((e) => e.clientId));
  return {
    ramais: empresas.reduce((s, e) => s + e.ramais, 0),
    ddrs: empresas.reduce((s, e) => s + e.ddrs, 0),
    pool: rr.pool,
    mrr,
    proprio: Math.round((mrr - rr.pool) * 100) / 100,
    empresas,
    divergente: rr.divergente === true,
  };
}
